import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

interface FlyoutProps {
  children: ReactNode;
  heading: string;
  subtitle?: string;
  open: boolean;
  footer?: ReactNode;
  onClose: () => void;
  /** Panel width cap; defaults to 48rem. */
  maxWidth?: string;
}

export const Flyout = ({ children, footer, heading, subtitle, onClose, open, maxWidth = '48rem' }: FlyoutProps) => {
  const panelRef = useRef<HTMLElement>(null);
  // Keep the latest onClose without making it an effect dependency: parents pass a fresh inline
  // onClose on every render, and re-running the open-effect would steal focus back to the first
  // focusable element on every keystroke.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;

    const focusable = (): HTMLElement[] =>
      Array.from(panel?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [])
        .filter((el) => el.offsetParent !== null);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onCloseRef.current(); return; }
      // Trap Tab within the modal so focus cannot reach the page behind it (aria-modal).
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) { event.preventDefault(); panel?.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey && (active === first || active === panel)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', onKeyDown);
    // Focus the first form field on open (friendlier than the header Close button); fall back to
    // the first focusable element, then the panel.
    const items = focusable();
    const firstField = items.find((el) => ['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName));
    (firstField ?? items[0] ?? panel)?.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = originalOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div role="presentation" style={{ position: 'fixed', inset: 0, zIndex: 'var(--mgo-zindex-dialog)', background: 'var(--mgo-color-bg-blanket)' }}>
      <aside
        ref={panelRef}
        aria-label={heading}
        aria-modal="true"
        className="mgo-stack mgo-background-body"
        role="dialog"
        tabIndex={-1}
        style={{ marginLeft: 'auto', height: '100%', width: '100%', maxWidth, borderLeft: '1px solid var(--mgo-color-grey-05)', boxShadow: 'var(--mgo-shadow-floating)', outline: 'none' }}
      >
        <header
          className="mgo-split mgo-align-start mgo-gap-l"
          style={{ borderBottom: '1px solid var(--mgo-color-grey-05)', padding: 'var(--mgo-space-50) var(--mgo-space-60)' }}
        >
          <div className="mgo-stack mgo-gap-xs" style={{ minWidth: 0 }}>
            <h2 className="mgo-weight-strong mgo-color-neutral" style={{ margin: 0, fontSize: 'var(--mgo-size-60)' }}>{heading}</h2>
            {subtitle && <p className="mgo-text-s mgo-color-muted" style={{ margin: 0 }}>{subtitle}</p>}
          </div>
          <button
            aria-label="Close"
            className="mgo-background-raised mgo-text-l mgo-color-neutral"
            style={{ borderRadius: 'var(--mgo-radius)', border: '1px solid var(--mgo-color-grey-05)', padding: 'var(--mgo-space-20) var(--mgo-space-30)', lineHeight: 1 }}
            type="button"
            onClick={onClose}
          >
            X
          </button>
        </header>
        <div style={{ minHeight: 0, flex: 1, overflowY: 'auto', padding: 'var(--mgo-space-50) var(--mgo-space-60)' }}>{children}</div>
        {footer && (
          <footer
            className="mgo-cluster mgo-justify-end mgo-gap-s"
            style={{ borderTop: '1px solid var(--mgo-color-grey-05)', padding: 'var(--mgo-space-40) var(--mgo-space-60)' }}
          >
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
};
